/**
 * Hook TanStack Query per il dominio Admin (E10.11).
 * Pattern identico a features/triage/hooks.ts (slice 3).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { fetchThreshold, updateThreshold } from './api.js';
import {
  fetchSsoSettings,
  updateSsoSettings,
  type SsoSettings,
  type SsoSettingsInput,
} from './sso-api.js';
import { fetchUiCapabilities, updateUiCapabilities } from './capabilities-api.js';
import {
  fetchRolePermissions,
  updateRolePermissions,
  createRole,
  fetchPermissionsCatalog,
} from './role-permissions-api.js';
import { fetchBranding, updateBranding } from './branding-api.js';
import { fetchPrefectDeployments, updateDeploymentSchedule } from './prefect-schedules-api.js';
import { deleteProductCatalog, type DeleteCatalogResult } from './catalog-admin-api.js';
import {
  fetchBatchEditSettings,
  updateBatchEditHoldThreshold,
  type BatchEditSettings,
} from './batch-edit-settings-api.js';
import {
  fetchTicketSettings,
  updateCustomerNoteMinWords,
  type TicketSettings,
} from './ticket-settings-api.js';
import {
  fetchAdminEnvFile,
  fetchAdminTools,
  type AdminEnvFile,
  type AdminTools,
} from './tools-api.js';
import type { ThresholdSettings, NotificationSeverity } from './types.js';
import type { RoleCapabilitiesMap } from './capabilities-types.js';
import type { RolePermissionsMap, PermissionsCatalog } from './role-permissions-types.js';
import type { Branding } from './branding-types.js';
import type { PrefectDeployment, UpdateSchedulePayload } from './prefect-schedules-types.js';

/** Chiavi query centralizzate per il dominio Admin. */
export const adminKeys = {
  threshold: () => ['admin', 'threshold'] as const,
  uiCapabilities: () => ['admin', 'ui-capabilities'] as const,
  rolePermissions: () => ['admin', 'role-permissions'] as const,
  permissionsCatalog: () => ['admin', 'permissions'] as const,
  branding: () => ['admin', 'branding'] as const,
  prefectDeployments: () => ['admin', 'prefect-deployments'] as const,
  tools: () => ['admin', 'tools'] as const,
  envFile: () => ['admin', 'tools', 'env-file'] as const,
  batchEditSettings: () => ['admin', 'batch-edit-settings'] as const,
  ticketSettings: () => ['admin', 'ticket-settings'] as const,
  sso: () => ['admin', 'sso'] as const,
} as const;

/**
 * Link agli strumenti dell'ambiente (Mailpit, UI DT) e disponibilità della password admin DT.
 * @param abilitato - false senza SYSTEM_CONFIGURATION (l'endpoint risponderebbe 403).
 */
export function useAdminTools(abilitato = true): UseQueryResult<AdminTools> {
  return useQuery({
    queryKey: adminKeys.tools(),
    queryFn: fetchAdminTools,
    enabled: abilitato,
  });
}

/**
 * SOLO DEV, da rimuovere prima della produzione: contenuto del `.env` del server. Letto solo su
 * richiesta esplicita (`abilitato`), mai tenuto in cache dopo la chiusura della sezione.
 */
export function useAdminEnvFile(abilitato: boolean): UseQueryResult<AdminEnvFile> {
  return useQuery({
    queryKey: adminKeys.envFile(),
    queryFn: fetchAdminEnvFile,
    enabled: abilitato,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/**
 * Recupera la soglia di severità per le notifiche.
 * @param abilitato - se false, la query non viene eseguita (per gating UI).
 */
export function useThreshold(abilitato = true): UseQueryResult<ThresholdSettings> {
  return useQuery({
    queryKey: adminKeys.threshold(),
    queryFn: fetchThreshold,
    enabled: abilitato,
  });
}

/**
 * Mutation per aggiornare la soglia di severità.
 * Invalida la query threshold dopo il successo.
 */
export function useUpdateThreshold(): UseMutationResult<
  ThresholdSettings,
  Error,
  NotificationSeverity
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateThreshold,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.threshold() });
    },
  });
}

/**
 * Recupera la mappa corrente ruolo → capacità UI.
 * Sempre abilitata: serve anche ai componenti non-admin (es. Layout).
 */
export function useUiCapabilities(): UseQueryResult<RoleCapabilitiesMap> {
  return useQuery({
    queryKey: adminKeys.uiCapabilities(),
    queryFn: fetchUiCapabilities,
  });
}

/**
 * Mutation per aggiornare la mappa ruolo → capacità UI.
 * Invalida la query ui-capabilities dopo il successo.
 */
export function useUpdateUiCapabilities(): UseMutationResult<
  RoleCapabilitiesMap,
  Error,
  RoleCapabilitiesMap
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUiCapabilities,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.uiCapabilities() });
    },
  });
}

/**
 * Recupera la mappa corrente ruolo → privilegi RBAC.
 * Avviata always-on dal Layout per popolare la cache letta da `usePermissions`
 * (gating UI di tutta l'app). Il parametro `abilitato` permette di disattivarla
 * dove non serve (es. matrice di configurazione admin gated).
 */
export function useRolePermissions(abilitato = true): UseQueryResult<RolePermissionsMap> {
  return useQuery({
    queryKey: adminKeys.rolePermissions(),
    queryFn: fetchRolePermissions,
    enabled: abilitato,
  });
}

/**
 * Recupera il catalogo dei privilegi assegnabili (canonici + legacy) per la matrice RBAC
 * raggruppata per risorsa (Task 6).
 * @param abilitato - se false, la query non viene eseguita (per gating UI).
 */
export function usePermissionsCatalog(abilitato = true): UseQueryResult<PermissionsCatalog> {
  return useQuery({
    queryKey: adminKeys.permissionsCatalog(),
    queryFn: fetchPermissionsCatalog,
    enabled: abilitato,
  });
}

/**
 * Mutation per aggiornare la mappa ruolo → privilegi RBAC.
 * Invalida la query role-permissions dopo il successo.
 */
export function useUpdateRolePermissions(): UseMutationResult<
  RolePermissionsMap,
  Error,
  RolePermissionsMap
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateRolePermissions,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.rolePermissions() });
    },
  });
}

/**
 * Mutation per creare un ruolo custom.
 * Invalida la query role-permissions dopo il successo per aggiornare la matrice.
 */
export function useCreateRole(): UseMutationResult<
  { name: string; isBuiltin: boolean },
  Error,
  { name: string; basedOn?: string[] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, basedOn }) => createRole(name, basedOn),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.rolePermissions() });
    },
  });
}

/**
 * Recupera il branding (logo) corrente.
 * Sempre abilitata: serve all'header (Layout) per tutti i ruoli.
 */
export function useBranding(): UseQueryResult<Branding> {
  return useQuery({
    queryKey: adminKeys.branding(),
    queryFn: fetchBranding,
  });
}

/**
 * Mutation per impostare il logo del portale. Invalida la query branding al successo.
 */
export function useUpdateBranding(): UseMutationResult<Branding, Error, string | null> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBranding,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.branding() });
    },
  });
}

/**
 * Danger Zone: elimina l'intero catalogo prodotti. Invalida TUTTE le query (non solo
 * il dominio Admin): l'azione svuota products/releases/bundle, letti da più feature.
 */
export function useDeleteProductCatalog(): UseMutationResult<DeleteCatalogResult, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProductCatalog,
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

/**
 * Recupera i deployment Prefect con il loro schedule cron.
 * @param abilitato - se false, la query non viene eseguita (gating: solo admin).
 */
export function usePrefectDeployments(abilitato = true): UseQueryResult<PrefectDeployment[]> {
  return useQuery({
    queryKey: adminKeys.prefectDeployments(),
    queryFn: fetchPrefectDeployments,
    enabled: abilitato,
  });
}

/**
 * Mutation per aggiornare lo schedule cron di un deployment. Invalida la lista al successo.
 */
export function useUpdateDeploymentSchedule(): UseMutationResult<
  { name: string; cron: string; timezone: string; active: boolean },
  Error,
  UpdateSchedulePayload
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDeploymentSchedule,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.prefectDeployments() });
    },
  });
}

/**
 * Soglia di hold della Batch Edit API.
 * @param abilitato - false senza SYSTEM_CONFIGURATION (sezione non mostrata).
 */
export function useBatchEditSettings(abilitato = true): UseQueryResult<BatchEditSettings> {
  return useQuery({
    queryKey: adminKeys.batchEditSettings(),
    queryFn: fetchBatchEditSettings,
    enabled: abilitato,
  });
}

/** Mutation: salva la soglia di hold (aggiorna anche la cache letta dal dettaglio batch). */
export function useUpdateBatchEditHoldThreshold(): UseMutationResult<
  BatchEditSettings,
  Error,
  number
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBatchEditHoldThreshold,
    onSuccess: (data) => {
      queryClient.setQueryData(adminKeys.batchEditSettings(), data);
      void queryClient.invalidateQueries({ queryKey: ['batch-edits', 'hold-threshold'] });
    },
  });
}

/**
 * Impostazioni dei Ticket (parole minime della nota per il cliente).
 * @param abilitato - false senza SYSTEM_CONFIGURATION (sezione non mostrata).
 */
export function useTicketSettings(abilitato = true): UseQueryResult<TicketSettings> {
  return useQuery({
    queryKey: adminKeys.ticketSettings(),
    queryFn: fetchTicketSettings,
    enabled: abilitato,
  });
}

/** Mutation: salva il minimo di parole (aggiorna anche la cache letta dal dialog di chiusura). */
export function useUpdateCustomerNoteMinWords(): UseMutationResult<TicketSettings, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCustomerNoteMinWords,
    onSuccess: (data) => {
      queryClient.setQueryData(adminKeys.ticketSettings(), data);
      void queryClient.invalidateQueries({ queryKey: ['triage', 'customer-note-settings'] });
    },
  });
}

/** Query: impostazioni SSO Entra ID (TC_AUTH_PROVIDER=entra; solo admin). */
export function useSsoSettings(abilitato = true): UseQueryResult<SsoSettings> {
  return useQuery({
    queryKey: adminKeys.sso(),
    queryFn: fetchSsoSettings,
    enabled: abilitato,
    // 404 = product-service senza login Entra (AUTH_BROKER_ENABLED): niente retry.
    retry: false,
  });
}

/** Mutation: salva le impostazioni SSO. */
export function useUpdateSsoSettings(): UseMutationResult<SsoSettings, Error, SsoSettingsInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSsoSettings,
    onSuccess: (data) => {
      queryClient.setQueryData(adminKeys.sso(), data);
    },
  });
}
